import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DirectionProvider, RouteLoader } from "@envirohub/ui";
import { DocumentMeta } from "@/components/DocumentMeta";
import { PrivateRoute } from "@/components/PrivateRoute";

// Layouts
import PublicLayout from "./layouts/PublicLayout";
import PortalLayout from "./layouts/PortalLayout";

// Eagerly loaded pages (auth must be fast)
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import RegisterOrganizationPage from "./pages/auth/RegisterOrganizationPage";
import OtpPage from "./pages/auth/OtpPage";
import ForgotPassword from "./pages/auth/ForgotPasswordPage";
import ChangePasswordPage from "./pages/auth/ChangePasswordPage";
import InviteAcceptPage from "./pages/auth/InviteAcceptPage";

// Lazy loaded portal pages
const Dashboard = lazy(() => import("./pages/portal/DashboardPage"));
const Services = lazy(() => import("./pages/portal/ServicesPage"));
const ServiceDetail = lazy(() => import("./pages/portal/ServiceDetailPage"));
const VendorList = lazy(() => import("./pages/portal/VendorListPage"));
const VendorOfferDetail = lazy(() => import("./pages/portal/VendorOfferDetailPage"));
const OrderDetail = lazy(() => import("./pages/portal/OrderDetailPage"));
const Orders = lazy(() => import("./pages/portal/OrdersPage"));
const Requests = lazy(() => import("./pages/portal/RequestsPage"));
const BiddingRoom = lazy(() => import("./pages/portal/BiddingRoomPage"));
const Checkout = lazy(() => import("./pages/portal/orders/CheckoutPage"));
const PaymentsReturn = lazy(() => import("./pages/portal/orders/PaymentsReturnPage"));
const Contact = lazy(() => import("./pages/portal/ContactPage"));
const ProfileLayout = lazy(() => import("./pages/portal/profile/ProfileLayout"));
const ProfileAccount = lazy(() => import("./pages/portal/profile/ProfileAccountPage"));
const ProfileLocations = lazy(() => import("./pages/portal/profile/ProfileLocationsPage"));
const ProfileWallet = lazy(() => import("./pages/portal/profile/ProfileWalletPage"));
const ProfileTeam = lazy(() => import("./pages/portal/profile/ProfileTeamPage"));
const ProfileSecurity = lazy(() => import("./pages/portal/profile/ProfileSecurityPage"));
const NotFound = lazy(() => import("./pages/NotFoundPage"));
const NotAllowed = lazy(() => import("./pages/NotAllowedPage"));

// New Order Wizard lazy loaded
const NewOrderLayout = lazy(() => import("./pages/portal/orders/new/NewOrderLayout"));
const Step1Configuration = lazy(() => import("./pages/portal/orders/new/steps/Step1Configuration"));
const Step2Location = lazy(() => import("./pages/portal/orders/new/steps/Step2Location"));
const Step3Details = lazy(() => import("./pages/portal/orders/new/steps/Step3Details"));
const Step4Review = lazy(() => import("./pages/portal/orders/new/steps/Step4Review"));

function OrdersNewIndexRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "configuration", search }} replace />;
}

export default function App() {
  const { i18n } = useTranslation();
  // Keep dir in sync if language changes at runtime
  React.useEffect(() => {
    document.documentElement.setAttribute("dir", i18n.language === "ar" ? "rtl" : "ltr");
  }, [i18n.language]);

  return (
    <DirectionProvider dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <BrowserRouter>
        <DocumentMeta />
        <Suspense fallback={<RouteLoader fullScreen />}>
          <Routes>
            {/* ── Auth routes ───────────────────────────────────── */}
            <Route element={<PublicLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register/organization" element={<RegisterOrganizationPage />} />
              <Route path="/verify" element={<OtpPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/invite/accept" element={<InviteAcceptPage />} />
              <Route
                path="/change-password"
                element={
                  <PrivateRoute>
                    <ChangePasswordPage />
                  </PrivateRoute>
                }
              />
            </Route>

            {/* ── Protected portal routes ───────────────────────── */}
            <Route
              element={
                <PrivateRoute>
                  <PortalLayout />
                </PrivateRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:id" element={<ServiceDetail />} />
              <Route path="/requests" element={<Requests />} />
              <Route path="/requests/:id" element={<BiddingRoom />} />
              <Route path="/requests/:id/vendors" element={<VendorList />} />
              <Route path="/requests/:requestId/vendors/:offerId" element={<VendorOfferDetail />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:id" element={<OrderDetail />} />
              <Route path="/orders/:id/checkout" element={<Checkout />} />
              <Route path="/payments/return" element={<PaymentsReturn />} />

              {/* Multi-step Create Request Wizard */}
              <Route path="/orders/new" element={<NewOrderLayout />}>
                <Route index element={<OrdersNewIndexRedirect />} />
                <Route path="configuration" element={<Step1Configuration />} />
                <Route path="location" element={<Step2Location />} />
                <Route path="details" element={<Step3Details />} />
                <Route path="review" element={<Step4Review />} />
              </Route>

              <Route path="/locations" element={<Navigate to="/profile/locations" replace />} />
              <Route path="/wallet" element={<Navigate to="/profile/wallet" replace />} />
              <Route path="/support" element={<Navigate to="/contact" replace />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/profile" element={<ProfileLayout />}>
                <Route index element={<Navigate to="account" replace />} />
                <Route path="account" element={<ProfileAccount />} />
                <Route path="locations" element={<ProfileLocations />} />
                <Route path="wallet" element={<ProfileWallet />} />
                <Route path="team" element={<ProfileTeam />} />
                <Route path="security" element={<ProfileSecurity />} />
              </Route>
              <Route path="/not-allowed" element={<NotAllowed />} />
            </Route>

            {/* ── 404 ───────────────────────────────────────────── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </DirectionProvider>
  );
}
